import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2 } from 'lucide-react';
import { NavigationBar } from '../../components/ui/NavigationBar';
import { submitContactForm } from '../../lib/r2';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { AlertModal } from '../../components/ui/AlertModal';
import { useTranslations } from '../../i18n';

export function ContactUsForm() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [sending, setSending] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
    const { t } = useTranslations();
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    // iOS Keyboard Fix: Use Visual Viewport API
    useEffect(() => {
        if (!window.visualViewport) return;

        const vvp = window.visualViewport;
        const handleViewportChange = () => {
            if (containerRef.current) {
                containerRef.current.style.height = `${vvp.height}px`;
                containerRef.current.style.top = `${vvp.offsetTop}px`;
                if (window.scrollY !== 0) {
                    window.scrollTo(0, 0);
                }
            }
        };

        vvp.addEventListener('resize', handleViewportChange);
        vvp.addEventListener('scroll', handleViewportChange);
        handleViewportChange();

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100dvh';

        return () => {
            vvp.removeEventListener('resize', handleViewportChange);
            vvp.removeEventListener('scroll', handleViewportChange);
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        };
    }, []);

    const handleSend = async () => {
        if (!title.trim()) {
            setAlertConfig({ isOpen: true, title: t.common.error, message: t.settings.enterSubjectAlert });
            return;
        }
        if (!content.trim()) {
            setAlertConfig({ isOpen: true, title: t.common.error, message: t.settings.enterMessageAlert });
            return;
        }

        setSending(true);
        try {
            await submitContactForm(title.trim(), content.trim());
            setSubmitted(true);
        } catch (error) {
            console.error('Error sending message:', error);
            setAlertConfig({ isOpen: true, title: t.common.error, message: t.settings.sendFailedAlert || 'Failed to send' });
        } finally {
            setSending(false);
        }
    };

    if (submitted) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center p-6 text-center transition-colors overflow-hidden bg-gradient-theme">
                <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-6">
                    <Send size={32} />
                </div>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{t.settings.messageSent}</h2>
                <p className="text-gray-500 dark:text-gray-400 font-medium mb-8 max-w-xs">
                    {t.settings.feedbackThanks}
                </p>
                <button
                    onClick={() => navigate('/settings')}
                    className="w-full max-w-xs py-4 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-lg shadow-blue-500/30 active:scale-95 transition-all text-sm uppercase tracking-widest"
                >
                    {t.settings.backToSettings}
                </button>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 flex flex-col overflow-hidden text-gray-900 dark:text-gray-100 z-[100] bg-gradient-theme"
        >
            <div className="flex-shrink-0 z-30">
                <NavigationBar
                    title={t.settings.contactUs}
                    onBack={() => navigate(-1)}
                    fixed={false}
                    rightAction={{
                        icon: sending ? Loader2 : Send,
                        onClick: handleSend,
                        disabled: sending || !title.trim() || !content.trim(),
                        className: sending ? 'animate-spin' : ''
                    }}
                />
            </div>

            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto"
            >
                <div className="p-4 space-y-6 pb-20 max-w-2xl mx-auto">
                    <div className="space-y-4">
                        <div>
                            <h1 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">{t.settings.contactSupport}</h1>
                            <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">{t.settings.contactDesc}</p>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{t.settings.subjectLabel}</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t.settings.subjectPlaceholder}
                            className="w-full px-4 py-4 bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-3xl text-gray-900 dark:text-white font-bold text-lg placeholder:text-gray-300 dark:placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all shadow-sm"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{t.settings.messageLabel}</label>
                        <RichTextEditor
                            content={content}
                            onChange={setContent}
                            placeholder={t.settings.messagePlaceholder}
                            minHeight="300px"
                            className="rounded-3xl border-gray-100 dark:border-white/10"
                        />
                    </div>
                </div>
                {/* Extra space for keyboard */}
                <div className="h-[40vh]" />
            </div>
            <AlertModal
                isOpen={alertConfig.isOpen}
                title={alertConfig.title}
                message={alertConfig.message}
                confirmLabel={t.common.ok}
                onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
            />
        </div>
    );
}


